// theme.js
import { createTheme, alpha } from '@mui/material/styles';

// Modern color palette
const colors = {
  primary: {
    main: '#FFD700',
    light: '#FFE55C',
    dark: '#DAA520',
    contrastText: '#000000',
  },
  secondary: {
    main: '#1A1A1A',
    light: '#2D2D2D',
    dark: '#0D0D0D',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#000000',
    paper: '#0D0D0D',
    card: '#1A1A1A',
  },
  text: {
    primary: '#FFFFFF',
    secondary: '#B3B3B3',
    disabled: '#6B6B6B',
  },
  success: {
    main: '#4CAF50',
    light: '#81C784',
    dark: '#388E3C',
  },
  error: {
    main: '#FF3D00',
    light: '#FF6B3D',
    dark: '#D32F2F',
  },
  warning: {
    main: '#FF9800',
    light: '#FFB74D',
    dark: '#F57C00',
  },
  info: {
    main: '#2196F3',
    light: '#64B5F6',
    dark: '#1976D2',
  },
};

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: colors.primary,
    secondary: colors.secondary,
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: colors.text,
    success: colors.success,
    error: colors.error,
    warning: colors.warning,
    info: colors.info,
  },
  typography: {
    fontFamily: "'Cairo', 'Poppins', 'Segoe UI', sans-serif",
    h1: {
      fontWeight: 800,
      fontSize: '3.5rem',
      letterSpacing: '-0.02em',
      background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.5rem',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 700,
      fontSize: '2rem',
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.75rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 16,
  },
  shadows: [
    'none',
    '0 2px 4px rgba(0,0,0,0.1)',
    '0 4px 8px rgba(0,0,0,0.12)',
    '0 8px 16px rgba(0,0,0,0.14)',
    '0 12px 24px rgba(0,0,0,0.16)',
    '0 16px 32px rgba(0,0,0,0.18)',
    '0 20px 40px rgba(0,0,0,0.2)',
    '0 24px 48px rgba(0,0,0,0.22)',
    '0 28px 56px rgba(0,0,0,0.24)',
    '0 32px 64px rgba(0,0,0,0.26)',
    '0 36px 72px rgba(0,0,0,0.28)',
    '0 40px 80px rgba(0,0,0,0.3)',
    '0 44px 88px rgba(0,0,0,0.32)',
    '0 48px 96px rgba(0,0,0,0.34)',
    '0 52px 104px rgba(0,0,0,0.36)',
    '0 56px 112px rgba(0,0,0,0.38)',
    '0 60px 120px rgba(0,0,0,0.4)',
    '0 64px 128px rgba(0,0,0,0.42)',
    '0 68px 136px rgba(0,0,0,0.44)',
    '0 72px 144px rgba(0,0,0,0.46)',
    '0 76px 152px rgba(0,0,0,0.48)',
    '0 80px 160px rgba(0,0,0,0.5)',
    '0 84px 168px rgba(0,0,0,0.52)',
    '0 88px 176px rgba(0,0,0,0.54)',
    '0 92px 184px rgba(0,0,0,0.56)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: `${colors.primary.main} ${colors.background.card}`,
          },
          '::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '::-webkit-scrollbar-track': {
            background: colors.background.card,
            borderRadius: '10px',
          },
          '::-webkit-scrollbar-thumb': {
            background: colors.primary.main,
            borderRadius: '10px',
            '&:hover': {
              background: colors.primary.light,
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          padding: '10px 24px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
          color: '#000000',
          fontWeight: 700,
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
            boxShadow: `0 8px 20px ${alpha(colors.primary.main, 0.3)}`,
          },
        },
        outlined: {
          borderColor: colors.primary.main,
          color: colors.primary.main,
          '&:hover': {
            borderColor: colors.primary.light,
            backgroundColor: alpha(colors.primary.main, 0.1),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.card,
          borderRadius: '20px',
          border: `1px solid ${alpha(colors.primary.main, 0.1)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: colors.primary.main,
            transform: 'translateY(-4px)',
            boxShadow: `0 12px 32px ${alpha(colors.primary.main, 0.15)}`,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          transition: 'all 0.3s ease',
        },
        elevation1: {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
        elevation2: {
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        },
        elevation3: {
          boxShadow: '0 12px 36px rgba(0,0,0,0.14)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            transition: 'all 0.3s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary.main,
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          fontWeight: 600,
          transition: 'all 0.3s ease',
        },
        colorPrimary: {
          backgroundColor: colors.primary.main,
          color: '#000000',
        },
        colorSecondary: {
          backgroundColor: colors.secondary.light,
          color: '#FFFFFF',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '24px',
          background: `linear-gradient(135deg, ${colors.background.paper} 0%, ${colors.background.card} 100%)`,
          border: `1px solid ${alpha(colors.primary.main, 0.2)}`,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          border: '1px solid',
        },
        standardSuccess: {
          backgroundColor: alpha(colors.success.main, 0.1),
          borderColor: colors.success.main,
          color: colors.success.light,
        },
        standardError: {
          backgroundColor: alpha(colors.error.main, 0.1),
          borderColor: colors.error.main,
          color: colors.error.light,
        },
        standardWarning: {
          backgroundColor: alpha(colors.warning.main, 0.1),
          borderColor: colors.warning.main,
          color: colors.warning.light,
        },
        standardInfo: {
          backgroundColor: alpha(colors.info.main, 0.1),
          borderColor: colors.info.main,
          color: colors.info.light,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.card,
          '& .MuiTableCell-root': {
            fontWeight: 700,
            color: colors.primary.main,
            borderBottom: `2px solid ${alpha(colors.primary.main, 0.2)}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${alpha(colors.primary.main, 0.1)}`,
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          background: `linear-gradient(180deg, ${colors.background.paper} 0%, ${colors.background.card} 100%)`,
          borderTop: `2px solid ${colors.primary.main}`,
          backdropFilter: 'blur(10px)',
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
          transition: 'all 0.3s ease',
          '&.Mui-selected': {
            color: colors.primary.main,
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.75rem',
              fontWeight: 700,
            },
          },
          '&:hover': {
            color: colors.primary.main,
            transform: 'translateY(-2px)',
          },
        },
      },
    },
  },
});

// Custom global styles
export const globalStyles = {
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(30px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  '@keyframes slideInLeft': {
    '0%': {
      opacity: 0,
      transform: 'translateX(-30px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
  '@keyframes slideInRight': {
    '0%': {
      opacity: 0,
      transform: 'translateX(30px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.05)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
  '@keyframes shimmer': {
    '0%': {
      backgroundPosition: '-200% 0',
    },
    '100%': {
      backgroundPosition: '200% 0',
    },
  },
  '@keyframes float': {
    '0%': {
      transform: 'translateY(0px)',
    },
    '50%': {
      transform: 'translateY(-10px)',
    },
    '100%': {
      transform: 'translateY(0px)',
    },
  },
};